import React, { useEffect, useRef, useState } from 'react';
import siteAnalyticsService, { BehaviorResponse } from '../../services/siteAnalytics.service';
import { useDateRange, RangeSelector, LoadError, fa, fmtDuration, decodePath } from './useDateRange';
import './analytics.css';

const SiteAnalyticsBehavior: React.FC = () => {
  const dr = useDateRange('30d');
  const [data, setData] = useState<BehaviorResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const reqIdRef = useRef(0);

  const load = async () => {
    const reqId = ++reqIdRef.current;
    setLoading(true);
    setErr(null);
    try {
      const r = await siteAnalyticsService.getBehavior(dr.range);
      if (reqId !== reqIdRef.current) return;
      setData(r);
    } catch (e: any) {
      if (reqId !== reqIdRef.current) return;
      setErr(e?.message || 'خطا در دریافت داده');
    } finally {
      if (reqId === reqIdRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dr.range.from, dr.range.to, dr.refreshKey]);

  return (
    <div className="analytics-page">
      <div className="analytics-header">
        <h2>رفتار کاربران</h2>
        <div className="analytics-controls">
          <RangeSelector {...dr} />
          <button onClick={dr.refresh} disabled={loading}>
            {loading ? 'در حال بارگذاری…' : 'بروزرسانی'}
          </button>
        </div>
      </div>

      {err && <LoadError message={err} onRetry={load} />}
      {loading && !data && <div className="analytics-loading">در حال بارگذاری…</div>}

      {data && (
        <>
          <div className="analytics-section">
            <div className="analytics-section-header">
              <h3>صفحات پربازدید (Top Pages)</h3>
            </div>
            {data.topPages.length === 0 ? (
              <div className="analytics-empty">داده‌ای نیست</div>
            ) : (
              <table className="analytics-table">
                <thead>
                  <tr>
                    <th>صفحه</th>
                    <th>بازدید کل</th>
                    <th>بازدید منحصربه‌فرد</th>
                    <th>کاربر</th>
                    <th>میانگین زمان</th>
                  </tr>
                </thead>
                <tbody>
                  {data.topPages.map((r) => (
                    <tr key={r.value}>
                      <td>
                        {r.title && (
                          <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 2 }}>{r.title}</div>
                        )}
                        <div style={{ direction: 'ltr', textAlign: 'right', fontSize: 11, color: 'var(--color-text-secondary)' }}>
                          {decodePath(r.value) || '/'}
                        </div>
                      </td>
                      <td>{fa(r.pageViews)}</td>
                      <td>{fa(r.uniquePageViews)}</td>
                      <td>{fa(r.visitors)}</td>
                      <td>{fmtDuration(r.avgDuration)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="analytics-grid-2col">
            <div className="analytics-section">
              <div className="analytics-section-header">
                <h3>صفحات ورود (Top Landing)</h3>
              </div>
              {data.topLanding.length === 0 ? (
                <div className="analytics-empty">داده‌ای نیست</div>
              ) : (
                <table className="analytics-table">
                  <thead>
                    <tr>
                      <th>مسیر</th>
                      <th>سشن</th>
                      <th>Bounce</th>
                      <th>میانگین مدت</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.topLanding.map((r) => (
                      <tr key={r.value}>
                        <td style={{ direction: 'ltr', textAlign: 'right', fontSize: 12 }}>{decodePath(r.value) || '/'}</td>
                        <td>{fa(r.sessions)}</td>
                        <td>{fa(r.bounceRate)}٪</td>
                        <td>{fmtDuration(r.avgDuration)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="analytics-section">
              <div className="analytics-section-header">
                <h3>صفحات خروج (Top Exit)</h3>
              </div>
              {data.topExit.length === 0 ? (
                <div className="analytics-empty">داده‌ای نیست</div>
              ) : (
                <table className="analytics-table">
                  <thead>
                    <tr>
                      <th>مسیر</th>
                      <th>تعداد خروج</th>
                      <th>نرخ خروج</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.topExit.map((r) => (
                      <tr key={r.path}>
                        <td style={{ direction: 'ltr', textAlign: 'right', fontSize: 12 }}>{decodePath(r.path) || '/'}</td>
                        <td>{fa(r.count)}</td>
                        <td>
                          {typeof r.exitRatePct === 'number' ? (
                            <span
                              style={{
                                fontWeight: 700,
                                color:
                                  r.exitRatePct >= 60
                                    ? 'var(--color-error-dark)'
                                    : r.exitRatePct >= 30
                                    ? 'var(--color-warning-dark)'
                                    : 'var(--color-text-secondary)',
                              }}
                            >
                              {fa(r.exitRatePct)}٪
                            </span>
                          ) : (
                            '—'
                          )}
                          {typeof r.views === 'number' && (
                            <span style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}> از {fa(r.views)}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          <div className="analytics-grid-2col">
            <div className="analytics-section">
              <div className="analytics-section-header">
                <h3>بیشترین زمان روی صفحه</h3>
              </div>
              {data.avgTime.length === 0 ? (
                <div className="analytics-empty">داده‌ای نیست</div>
              ) : (
                <table className="analytics-table">
                  <thead>
                    <tr>
                      <th>مسیر</th>
                      <th>میانگین زمان</th>
                      <th>نمونه</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.avgTime.map((r) => (
                      <tr key={r.path}>
                        <td style={{ direction: 'ltr', textAlign: 'right', fontSize: 12 }}>{decodePath(r.path)}</td>
                        <td>{fmtDuration(r.avgTime)}</td>
                        <td>{fa(r.views)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="analytics-section">
              <div className="analytics-section-header">
                <h3>بیشترین Scroll Depth</h3>
              </div>
              {data.scrollDepth.length === 0 ? (
                <div className="analytics-empty">داده‌ای نیست</div>
              ) : (
                <table className="analytics-table">
                  <thead>
                    <tr>
                      <th>مسیر</th>
                      <th>میانگین %</th>
                      <th>نمونه</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.scrollDepth.map((r) => (
                      <tr key={r.path}>
                        <td style={{ direction: 'ltr', textAlign: 'right', fontSize: 12 }}>{decodePath(r.path)}</td>
                        <td>{fa(r.avgScroll)}٪</td>
                        <td>{fa(r.views)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          <div className="analytics-section">
            <div className="analytics-section-header">
              <h3>صفحات ۴۰۴ احتمالی</h3>
            </div>
            {data.top404.length === 0 ? (
              <div className="analytics-empty">خوبه! صفحه ۴۰۴ زیادی نداریم.</div>
            ) : (
              <table className="analytics-table">
                <thead>
                  <tr>
                    <th>مسیر</th>
                    <th>تعداد</th>
                  </tr>
                </thead>
                <tbody>
                  {data.top404.map((r) => (
                    <tr key={r.path}>
                      <td style={{ direction: 'ltr', textAlign: 'right', fontSize: 12 }}>{decodePath(r.path)}</td>
                      <td>{fa(r.count)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Heatmap بازدید: روزِ هفته × ساعت */}
          <div className="analytics-section">
            <div className="analytics-section-header">
              <h3>الگوی بازدید (روز هفته × ساعت)</h3>
            </div>
            {!data.heatmap || data.heatmap.length === 0 ? (
              <div className="analytics-empty">داده‌ای نیست</div>
            ) : (
              <Heatmap cells={data.heatmap} />
            )}
          </div>
        </>
      )}
    </div>
  );
};

// ایندکس بک‌اند: 0=یکشنبه … 6=شنبه
const DAY_FA = ['یکشنبه', 'دوشنبه', 'سه‌شنبه', 'چهارشنبه', 'پنجشنبه', 'جمعه', 'شنبه'];
// هفتهٔ ایرانی از شنبه شروع می‌شود → ترتیب نمایش ردیف‌ها: شنبه، یکشنبه، …، جمعه
const DAY_ORDER = [6, 0, 1, 2, 3, 4, 5];

const Heatmap: React.FC<{ cells: Array<{ dayOfWeek: number; hour: number; count: number }> }> = ({ cells }) => {
  const grid: number[][] = Array.from({ length: 7 }, () => new Array(24).fill(0));
  let max = 0;
  for (const c of cells) {
    if (c.dayOfWeek >= 0 && c.dayOfWeek < 7 && c.hour >= 0 && c.hour < 24) {
      grid[c.dayOfWeek][c.hour] = c.count;
      if (c.count > max) max = c.count;
    }
  }
  const color = (n: number) => {
    // توکن واقعی تم — var قبلی تعریف نشده بود و سلول خالی در دارک‌مود روشن می‌ماند
    if (!n || !max) return 'var(--color-bg-default, #f4f4f5)';
    const a = 0.12 + (n / max) * 0.88;
    return `rgba(99, 102, 241, ${a.toFixed(2)})`;
  };
  return (
    <div style={{ overflowX: 'auto' }}>
      <table className="analytics-table" style={{ borderCollapse: 'separate', borderSpacing: 2 }}>
        <thead>
          <tr>
            <th></th>
            {Array.from({ length: 24 }, (_, h) => (
              <th key={h} style={{ fontSize: 10, textAlign: 'center', padding: 2 }}>
                {fa(h)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {DAY_ORDER.map((d) => (
            <tr key={d}>
              <td style={{ fontSize: 12, whiteSpace: 'nowrap', fontWeight: 500 }}>{DAY_FA[d]}</td>
              {grid[d].map((n, h) => (
                <td
                  key={h}
                  title={`${DAY_FA[d]} ساعت ${fa(h)} — ${fa(n)} بازدید`}
                  style={{
                    width: 22,
                    height: 22,
                    padding: 0,
                    textAlign: 'center',
                    background: color(n),
                    borderRadius: 4,
                  }}
                />
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default SiteAnalyticsBehavior;
